<template>
    <section class="dp-layout erm-page">
        <section class="dp-left">
            <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
        </section>

        <section class="dp-right erm-workspace">
            <aside class="erm-role-pane">
                <div class="erm-pane-head">
                    <div>
                        <div class="erm-pane-head__title">评价角色</div>
                        <div class="erm-pane-head__sub">共 {{ roles.length }} 个</div>
                    </div>
                    <el-button type="success" size="small" icon="el-icon-plus" @click="handleAddRole">新增</el-button>
                </div>
                <div class="erm-pane-search">
                    <el-input
                        v-model="roleKeyword"
                        size="small"
                        clearable
                        prefix-icon="el-icon-search"
                        placeholder="搜索角色">
                    </el-input>
                </div>
                <div class="erm-role-list" v-loading="roleLoading">
                    <button
                        v-for="role in filteredRoles"
                        :key="role.Id"
                        type="button"
                        class="erm-role-item"
                        :class="{ 'is-active': currentRole && currentRole.Id === role.Id, 'is-disabled': !role.Enabled }"
                        @click="handleRoleSelect(role)">
                        <span class="erm-role-item__avatar">{{ roleInitial(role.Name) }}</span>
                        <span class="erm-role-item__body">
                            <span class="erm-role-item__name">
                                {{ role.Name }}
                                <el-tag v-if="!role.Enabled" size="mini" type="danger" effect="dark" class="erm-role-item__status-tag">停用</el-tag>
                                <el-tag v-if="role.IsSystem" size="mini" type="info" effect="plain">系统</el-tag>
                            </span>
                            <span class="erm-role-item__meta">
                                {{ role.Code || '—' }}
                                · {{ roleMetaText(role) }}
                            </span>
                        </span>
                        <el-dropdown
                            v-if="!role.IsSystem"
                            trigger="click"
                            @command="cmd => handleRoleMenu(cmd, role)"
                            @click.native.stop>
                            <button type="button" class="erm-role-item__more" title="删除" @click.stop>
                                <i class="el-icon-more"></i>
                            </button>
                            <el-dropdown-menu slot="dropdown">
                                <el-dropdown-item command="delete">删除</el-dropdown-item>
                            </el-dropdown-menu>
                        </el-dropdown>
                    </button>
                    <el-empty v-if="!roleLoading && !filteredRoles.length" :image-size="56"
                        :description="roleKeyword ? '无匹配角色' : '暂无角色，请先新增'"></el-empty>
                </div>
            </aside>

            <main class="erm-member-pane">
                <div class="erm-member-head">
                    <div class="erm-member-head__main">
                        <div class="erm-member-head__title">
                            角色人员
                            <span v-if="currentRole" class="erm-role-badge">{{ currentRole.Name }}</span>
                        </div>
                        <div class="erm-member-head__sub" v-if="currentRole && isAllStaffRole">
                            共 <b>{{ memberTotal }}</b> 人
                            · 已排除 <b>{{ currentRole.ExcludedMemberCount || 0 }}</b> 人
                        </div>
                        <div class="erm-member-head__sub" v-else-if="currentRole && currentRole.NeedMemberList !== false">
                            共 <b>{{ memberTotal }}</b> 人
                            <span v-if="selectedMemberIds.length"> · 已选 <b>{{ selectedMemberIds.length }}</b> 人</span>
                        </div>
                        <div class="erm-member-head__sub" v-else-if="currentRole && currentRole.NeedMemberList === false">
                            该角色无需维护人员名单
                        </div>
                        <div class="erm-member-head__sub" v-else>请从左侧选择一个角色</div>
                    </div>
                    <div class="erm-member-head__actions" v-if="currentRole && !isAllStaffRole && currentRole.NeedMemberList !== false">
                        <el-dropdown trigger="click" @command="handleQuickImport">
                            <el-button size="small" type="warning">
                                快捷导入<i class="el-icon-arrow-down el-icon--right"></i>
                            </el-button>
                            <el-dropdown-menu slot="dropdown">
                                <el-dropdown-item command="ClassTeacher">从班主任导入</el-dropdown-item>
                                <el-dropdown-item command="HostelAdmin">从宿管导入</el-dropdown-item>
                                <el-dropdown-item command="DutyLeader">从值日领导导入</el-dropdown-item>
                            </el-dropdown-menu>
                        </el-dropdown>
                        <el-button size="small" type="danger" :disabled="!selectedMemberIds.length" @click="handleRemoveMembers">批量移除</el-button>
                        <el-button size="small" type="success" icon="el-icon-plus" @click="handleAddMembers">添加人员</el-button>
                    </div>
                </div>

                <div class="erm-member-body">
                    <el-empty v-if="!currentRole" :image-size="64" description="请从左侧选择一个角色"></el-empty>
                    <template v-else-if="currentRole.NeedMemberList === false && !isAllStaffRole">
                        <div class="erm-member-toolbar">
                            <div class="erm-role-actions">
                                <el-button size="small" type="info" icon="el-icon-edit" @click="handleEditRole(currentRole)">编辑</el-button>
                            </div>
                        </div>
                        <el-empty
                            :image-size="64"
                            :description="`「${currentRole.Name}」无需维护人员名单`">
                        </el-empty>
                    </template>
                    <template v-else>
                        <div class="erm-member-toolbar">
                            <el-input
                                v-model="memberKeyword"
                                size="small"
                                clearable
                                prefix-icon="el-icon-search"
                                :placeholder="isAllStaffRole ? '按姓名搜索教职工' : '按姓名搜索'"
                                class="erm-search-input"
                                @keyup.enter.native="handleMemberSearch"
                                @clear="handleMemberSearch">
                            </el-input>
                            <el-button size="small" type="primary" icon="el-icon-search" @click="handleMemberSearch">查询</el-button>
                            <div class="erm-role-actions">
                                <el-button size="small" type="info" icon="el-icon-edit" @click="handleEditRole(currentRole)">编辑</el-button>
                                <el-button
                                    v-if="!isAllStaffRole"
                                    size="small"
                                    :type="currentRole.Enabled ? 'warning' : 'success'"
                                    @click="toggleRoleEnabled(currentRole)">
                                    {{ currentRole.Enabled ? '停用' : '启用' }}
                                </el-button>
                            </div>
                        </div>
                        <div v-if="isAllStaffRole" class="erm-allstaff-tip">
                            默认本校全部教师自动具备「全体教职工」权限；禁用后该教师不再拥有仅依赖此角色的维度权限。
                        </div>
                        <div class="erm-member-table tbl-scroll-host">
                            <el-table
                                ref="memberTable"
                                :data="members"
                                v-loading="memberLoading"
                                class="custom-tbl"
                                style="width: 100%;"
                                height="100%"
                                size="small"
                                @row-click="handleMemberRowClick"
                                @selection-change="handleMemberSelectionChange">
                                <el-table-column v-if="!isAllStaffRole" type="selection" width="42"></el-table-column>
                                <el-table-column prop="TeacherName" label="教师姓名" min-width="100"></el-table-column>
                                <el-table-column prop="DepartmentName" label="部门" min-width="110" show-overflow-tooltip></el-table-column>
                                <el-table-column label="状态" width="88" align="center">
                                    <template slot-scope="scope">
                                        <el-tag
                                            :type="scope.row.Enabled !== false ? 'success' : 'info'"
                                            size="mini"
                                            effect="plain">
                                            {{ memberStatusText(scope.row) }}
                                        </el-tag>
                                    </template>
                                </el-table-column>
                                <el-table-column
                                    v-if="isAllStaffRole"
                                    label="操作"
                                    width="100"
                                    align="center"
                                    fixed="right">
                                    <template slot-scope="scope">
                                        <el-button
                                            v-if="scope.row.Enabled !== false"
                                            size="mini"
                                            type="warning"
                                            @click="toggleAllStaffMember(scope.row, false)">
                                            禁用
                                        </el-button>
                                        <el-button
                                            v-else
                                            size="mini"
                                            type="success"
                                            @click="toggleAllStaffMember(scope.row, true)">
                                            启用
                                        </el-button>
                                    </template>
                                </el-table-column>
                                <el-table-column
                                    v-else
                                    prop="CreateTime"
                                    label="加入时间"
                                    width="160"
                                    align="center">
                                </el-table-column>
                            </el-table>
                        </div>
                        <div class="erm-pagination">
                            <el-pagination
                                layout="total, prev, pager, next"
                                :current-page="memberPage"
                                :page-size="memberPageSize"
                                :total="memberTotal"
                                @current-change="handleMemberPageChange">
                            </el-pagination>
                        </div>
                    </template>
                </div>
            </main>

            <el-dialog
                :title="roleDialogTitle"
                :visible.sync="roleDialogVisible"
                width="420px"
                custom-class="dialog-theme"
                :close-on-click-modal="false"
                append-to-body>
                <el-form :model="roleForm" label-width="90px" :rules="roleFormRules" ref="roleForm" size="small">
                    <el-form-item label="角色名称" prop="Name">
                        <el-input v-model="roleForm.Name" maxlength="100"></el-input>
                    </el-form-item>
                    <el-form-item label="排序" prop="SortOrder">
                        <el-input-number
                            v-model="roleForm.SortOrder"
                            :min="0"
                            :max="9999"
                            controls-position="right"
                            style="width: 100%;">
                        </el-input-number>
                    </el-form-item>
                    <el-form-item v-if="roleForm.Id > 0 && roleForm.IsSystem" label="说明">
                        <span class="erm-hint">系统预置角色不可修改编码</span>
                    </el-form-item>
                </el-form>
                <div slot="footer" class="dialog-footer">
                    <el-button size="small" @click="roleDialogVisible = false">取消</el-button>
                    <el-button size="small" type="primary" :loading="roleSubmitLoading" @click="submitRoleForm">确定</el-button>
                </div>
            </el-dialog>

            <person-scope-picker-dialog
                :visible.sync="memberPickerVisible"
                directory-scope="internal"
                :show-scope-tabs="false"
                :multiple="true"
                :scope-department-id="positiveDeptId(DepartmentId)"
                title="添加角色人员"
                @confirm="onMembersPicked"
            />

            <el-dialog
                :title="importBrowseTitle"
                :visible.sync="importBrowseVisible"
                width="720px"
                custom-class="dialog-theme"
                :close-on-click-modal="false"
                append-to-body>
                <div class="erm-import-toolbar">
                    <el-input
                        v-model="importBrowseKeyword"
                        size="small"
                        clearable
                        prefix-icon="el-icon-search"
                        placeholder="按姓名搜索"
                        class="erm-search-input"
                        @input="handleImportBrowseSearch">
                    </el-input>
                    <span class="erm-import-count">
                        已选 <b>{{ importSelectedCount }}</b> 人
                        · 待导入 <b>{{ importPendingCount }}</b> 人
                        <span v-if="importAlreadyCount"> · 已在名单 <b>{{ importAlreadyCount }}</b> 人</span>
                    </span>
                </div>
                <div class="erm-import-table" v-loading="importBrowseLoading">
                    <el-table
                        ref="importBrowseTable"
                        :data="pagedImportCandidates"
                        size="small"
                        max-height="360"
                        class="custom-tbl"
                        style="width: 100%;"
                        row-key="TeacherMasterId"
                        @selection-change="handleImportSelectionChange">
                        <el-table-column
                            type="selection"
                            width="42"
                            :selectable="isImportCandidateSelectable">
                        </el-table-column>
                        <el-table-column prop="TeacherName" label="教师姓名" min-width="110"></el-table-column>
                        <el-table-column prop="DepartmentName" label="部门" min-width="120" show-overflow-tooltip></el-table-column>
                        <el-table-column label="状态" width="90" align="center">
                            <template slot-scope="scope">
                                <el-tag
                                    :type="scope.row.IsAlreadyMember ? 'info' : 'success'"
                                    size="mini"
                                    effect="plain">
                                    {{ scope.row.IsAlreadyMember ? '已在名单' : '待导入' }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column label="操作" width="90" align="center" fixed="right">
                            <template slot-scope="scope">
                                <el-button
                                    size="mini"
                                    type="danger"
                                    :disabled="scope.row.IsAlreadyMember"
                                    @click="removeImportCandidate(scope.row)">
                                    移除
                                </el-button>
                            </template>
                        </el-table-column>
                    </el-table>
                    <el-empty
                        v-if="!importBrowseLoading && !filteredImportCandidates.length"
                        :image-size="64"
                        :description="importBrowseKeyword ? '无匹配人员' : '暂无可导入人员'">
                    </el-empty>
                    <div class="erm-import-pagination" v-if="filteredImportCandidates.length">
                        <el-pagination
                            layout="total, sizes, prev, pager, next"
                            :current-page="importBrowsePage"
                            :page-size="importBrowsePageSize"
                            :page-sizes="[10, 20, 50, 100]"
                            :total="filteredImportCandidates.length"
                            @current-change="handleImportBrowsePageChange"
                            @size-change="handleImportBrowseSizeChange">
                        </el-pagination>
                    </div>
                </div>
                <div slot="footer" class="dialog-footer">
                    <el-button size="small" @click="importBrowseVisible = false">取消</el-button>
                    <el-button
                        size="small"
                        type="primary"
                        :loading="importBrowseSubmitLoading"
                        :disabled="!importSelectedCount"
                        @click="submitImportBrowse(false)">
                        导入选中（{{ importSelectedCount }}）
                    </el-button>
                    <el-button
                        size="small"
                        type="success"
                        :loading="importBrowseSubmitLoading"
                        :disabled="!importPendingCount"
                        @click="submitImportBrowse(true)">
                        导入全部（{{ importPendingCount }}）
                    </el-button>
                </div>
            </el-dialog>
        </section>
    </section>
</template>

<script>
import {
    getEvaluationRoles,
    createEvaluationRole,
    updateEvaluationRole,
    setEvaluationRoleEnabled,
    deleteEvaluationRole,
    getEvaluationRoleMembers,
    saveEvaluationRoleMembers,
    removeEvaluationRoleMembers,
    setEvaluationRoleMemberEnabled,
    previewQuickImportEvaluationRoleMembers
} from '../../api/modules/moral-education';
import DepartmentPane from '../../components/DepartmentPane';
import PersonScopePickerDialog from '../../components/PersonScopePickerDialog';
import { normalizeDeptId, treeNodeDeptId } from '../../utils/deptCascaderProps';

/** 评价角色与人员维护。最后更新：2026-09-25 */
export default {
    name: 'EvaluationRoleManage',
    components: { DepartmentPane, PersonScopePickerDialog },
    data() {
        return {
            DepartmentId: 0,
            roles: [],
            roleKeyword: '',
            roleLoading: false,
            currentRole: null,
            members: [],
            memberLoading: false,
            memberKeyword: '',
            memberPage: 1,
            memberPageSize: 10,
            memberTotal: 0,
            selectedMemberIds: [],
            roleDialogVisible: false,
            roleDialogTitle: '',
            roleSubmitLoading: false,
            roleForm: {
                Id: 0,
                Name: '',
                SortOrder: 0,
                IsSystem: false
            },
            roleFormRules: {
                Name: [{ required: true, message: '请输入角色名称', trigger: 'blur' }]
            },
            memberPickerVisible: false,
            memberSubmitLoading: false,
            importBrowseVisible: false,
            importBrowseLoading: false,
            importBrowseSubmitLoading: false,
            importBrowseSourceType: '',
            importBrowseKeyword: '',
            importCandidates: [],
            importBrowsePage: 1,
            importBrowsePageSize: 10,
            importSelectedMasterIds: [],
            importSelectionSyncing: false
        };
    },
    computed: {
        /** 当前选中的是否为全体教职工角色 */
        isAllStaffRole() {
            return !!(this.currentRole && this.currentRole.Code === 'AllStaff');
        },
        /** 快捷导入浏览弹窗标题 */
        importBrowseTitle() {
            const labelMap = {
                ClassTeacher: '班主任',
                HostelAdmin: '宿管',
                DutyLeader: '值日领导'
            };
            const sourceName = labelMap[this.importBrowseSourceType] || this.importBrowseSourceType || '来源';
            const roleName = (this.currentRole && this.currentRole.Name) || '当前角色';
            return `浏览导入人员 · 从「${sourceName}」导入到「${roleName}」`;
        },
        /** 快捷导入待导入人数（排除已在名单） */
        importPendingCount() {
            return (this.importCandidates || []).filter(item => !item.IsAlreadyMember).length;
        },
        /** 快捷导入已勾选人数 */
        importSelectedCount() {
            const pendingIds = new Set(
                (this.importCandidates || [])
                    .filter(item => !item.IsAlreadyMember)
                    .map(item => item.TeacherMasterId)
            );
            return (this.importSelectedMasterIds || []).filter(id => pendingIds.has(id)).length;
        },
        /** 快捷导入已在名单人数 */
        importAlreadyCount() {
            return (this.importCandidates || []).filter(item => item.IsAlreadyMember).length;
        },
        /** 快捷导入浏览列表本地搜索 */
        filteredImportCandidates() {
            const keyword = (this.importBrowseKeyword || '').trim().toLowerCase();
            const list = this.importCandidates || [];
            if (!keyword) return list;
            return list.filter(item => {
                const name = (item.TeacherName || '').toLowerCase();
                return name.indexOf(keyword) !== -1;
            });
        },
        /** 快捷导入浏览列表当前页数据 */
        pagedImportCandidates() {
            const list = this.filteredImportCandidates || [];
            const pageSize = this.importBrowsePageSize || 10;
            const maxPage = Math.max(1, Math.ceil(list.length / pageSize) || 1);
            const page = Math.min(Math.max(this.importBrowsePage || 1, 1), maxPage);
            const start = (page - 1) * pageSize;
            return list.slice(start, start + pageSize);
        },
        /** 按名称/编码过滤角色列表 */
        filteredRoles() {
            const keyword = (this.roleKeyword || '').trim().toLowerCase();
            if (!keyword) return this.roles;
            return this.roles.filter((role) => {
                const name = (role.Name || '').toLowerCase();
                const code = (role.Code || '').toLowerCase();
                return name.indexOf(keyword) !== -1 || code.indexOf(keyword) !== -1;
            });
        }
    },
    methods: {
        positiveDeptId(v) {
            const id = normalizeDeptId(v);
            return id || null;
        },
        treeNodeClick(data) {
            this.DepartmentId = treeNodeDeptId(data);
            this.currentRole = null;
            this.roleKeyword = '';
            this.members = [];
            this.loadRoles();
        },
        loadRoles() {
            const schoolId = this.positiveDeptId(this.DepartmentId);
            if (!schoolId) {
                this.roles = [];
                return;
            }
            this.roleLoading = true;
            getEvaluationRoles({ schoolDepartmentId: schoolId }).then(res => {
                this.roleLoading = false;
                if (res.data && res.data.success) {
                    this.roles = res.data.response || [];
                    if (this.currentRole) {
                        const matched = this.roles.find(r => r.Id === this.currentRole.Id);
                        if (matched) {
                            this.currentRole = matched;
                            this.loadMembers();
                        } else {
                            this.selectDefaultRole();
                        }
                    } else {
                        this.selectDefaultRole();
                    }
                } else {
                    this.roles = [];
                    this.$message.error((res.data && res.data.msg) || '加载角色失败');
                }
            }).catch(() => {
                this.roleLoading = false;
                this.roles = [];
            });
        },
        /** 默认选中角色：优先全体教职工，否则取第一个。创建于 2026-08-29 */
        selectDefaultRole() {
            if (!this.roles.length) {
                this.currentRole = null;
                this.members = [];
                this.memberTotal = 0;
                return;
            }
            const preferredRole = this.roles.find(r => r.Code === 'AllStaff') || this.roles[0];
            this.handleRoleSelect(preferredRole);
        },
        handleRoleSelect(row) {
            if (!row) return;
            this.currentRole = row;
            this.memberPage = 1;
            this.memberKeyword = '';
            this.selectedMemberIds = [];
            if (this.isAllStaffRole || row.NeedMemberList !== false) {
                this.loadMembers();
            } else {
                this.members = [];
                this.memberTotal = 0;
            }
        },
        /** 角色列表副标题文案 */
        roleMetaText(role) {
            if (!role) return '—';
            if (role.Code === 'AllStaff') {
                const excluded = role.ExcludedMemberCount || 0;
                return `${role.MemberCount || 0} 人 · 默认全员${excluded > 0 ? ` · 已排除 ${excluded}` : ''}`;
            }
            if (role.NeedMemberList === false) return '无需名单';
            return `${role.MemberCount || 0} 人`;
        },
        /** 成员状态文案 */
        memberStatusText(row) {
            if (this.isAllStaffRole) {
                return row && row.Enabled !== false ? '生效' : '已排除';
            }
            return row && row.Enabled !== false ? '启用' : '停用';
        },
        /** 角色名称首字，用于列表头像。创建于 2026-08-29 */
        roleInitial(name) {
            const text = (name || '').trim();
            return text ? text.charAt(0) : '?';
        },
        /** 角色列表更多菜单（仅自定义角色删除）。创建于 2026-08-29 */
        handleRoleMenu(command, role) {
            if (!role) return;
            if (command === 'delete') {
                this.handleDeleteRole(role);
            }
        },
        loadMembers() {
            const schoolId = this.positiveDeptId(this.DepartmentId);
            if (!schoolId || !this.currentRole || !this.currentRole.Id) return;
            this.memberLoading = true;
            getEvaluationRoleMembers({
                schoolDepartmentId: schoolId,
                roleId: this.currentRole.Id,
                keyword: this.memberKeyword || '',
                page: this.memberPage,
                pageSize: this.memberPageSize
            }).then(res => {
                this.memberLoading = false;
                if (res.data && res.data.success) {
                    const page = res.data.response || {};
                    this.members = page.data || page.Data || [];
                    this.memberTotal = page.dataCount || page.DataCount || 0;
                } else {
                    this.members = [];
                    this.memberTotal = 0;
                }
            }).catch(() => {
                this.memberLoading = false;
                this.members = [];
            });
        },
        handleMemberSearch() {
            this.memberPage = 1;
            this.loadMembers();
        },
        handleMemberPageChange(page) {
            this.memberPage = page;
            this.loadMembers();
        },
        handleMemberSelectionChange(rows) {
            this.selectedMemberIds = (rows || []).map(r => r.Id).filter(Boolean);
        },
        /** 点击行勾选，便于批量移除。最后修改：2026-09-25 */
        handleMemberRowClick(row, column, event) {
            if (this.isAllStaffRole || !row || !this.$refs.memberTable) return;
            if (column && column.type === 'selection') return;
            const target = event && event.target;
            if (target && target.closest && target.closest('.el-checkbox, .el-button, button')) return;
            this.$refs.memberTable.toggleRowSelection(row);
        },
        /** 全体教职工：禁用/恢复单个教师的默认权限 */
        toggleAllStaffMember(row, enabled) {
            const schoolId = this.positiveDeptId(this.DepartmentId);
            if (!schoolId || !this.currentRole || !row || !row.TeacherMasterId) return;
            const actionText = enabled ? '恢复' : '禁用';
            this.$confirm(`确认${actionText}教师「${row.TeacherName || ''}」的全体教职工权限吗？`, '提示', {
                type: 'warning'
            }).then(() => {
                this.memberLoading = true;
                setEvaluationRoleMemberEnabled({
                    SchoolDepartmentId: schoolId,
                    RoleId: this.currentRole.Id,
                    TeacherMasterId: row.TeacherMasterId,
                    Enabled: enabled
                }).then(res => {
                    this.memberLoading = false;
                    if (res.data && res.data.success) {
                        this.$message.success(res.data.msg || '操作成功');
                        this.loadRoles();
                        this.loadMembers();
                    } else {
                        this.$message.error((res.data && res.data.msg) || '操作失败');
                    }
                }).catch(() => {
                    this.memberLoading = false;
                });
            }).catch(() => { });
        },
        handleAddRole() {
            if (!this.positiveDeptId(this.DepartmentId)) {
                this.$message.warning('请先在左侧选择具体的学校机构！');
                return;
            }
            this.roleDialogTitle = '新增自定义角色';
            this.roleForm = { Id: 0, Name: '', SortOrder: 0, IsSystem: false };
            this.roleDialogVisible = true;
            this.$nextTick(() => {
                if (this.$refs.roleForm) this.$refs.roleForm.clearValidate();
            });
        },
        handleEditRole(row) {
            this.roleDialogTitle = '编辑角色';
            this.roleForm = {
                Id: row.Id,
                Name: row.Name,
                SortOrder: row.SortOrder || 0,
                IsSystem: !!row.IsSystem
            };
            this.roleDialogVisible = true;
        },
        submitRoleForm() {
            this.$refs.roleForm.validate(valid => {
                if (!valid) return;
                const schoolId = this.positiveDeptId(this.DepartmentId);
                if (!schoolId) return;
                this.roleSubmitLoading = true;
                const payload = {
                    SchoolDepartmentId: schoolId,
                    Id: this.roleForm.Id,
                    Name: this.roleForm.Name,
                    SortOrder: this.roleForm.SortOrder || 0
                };
                const req = this.roleForm.Id ? updateEvaluationRole(payload) : createEvaluationRole(payload);
                req.then(res => {
                    this.roleSubmitLoading = false;
                    if (res.data && res.data.success) {
                        this.$message.success(res.data.msg || '保存成功');
                        this.roleDialogVisible = false;
                        this.loadRoles();
                    } else {
                        this.$message.error((res.data && res.data.msg) || '保存失败');
                    }
                }).catch(() => {
                    this.roleSubmitLoading = false;
                });
            });
        },
        toggleRoleEnabled(row) {
            if (!row || row.Code === 'AllStaff') {
                this.$message.warning('全体教职工为默认权限角色，不允许停用');
                return;
            }
            const schoolId = this.positiveDeptId(this.DepartmentId);
            if (!schoolId) return;
            const nextEnabled = !row.Enabled;
            const actionText = nextEnabled ? '启用' : '停用';
            this.$confirm(`确认${actionText}角色「${row.Name || ''}」吗？`, '提示', {
                type: 'warning'
            }).then(() => {
                setEvaluationRoleEnabled({
                    SchoolDepartmentId: schoolId,
                    Id: row.Id,
                    Enabled: nextEnabled
                }).then(res => {
                    if (res.data && res.data.success) {
                        this.$message.success(res.data.msg || '操作成功');
                        this.loadRoles();
                    } else {
                        this.$message.error((res.data && res.data.msg) || '操作失败');
                    }
                });
            }).catch(() => { });
        },
        handleDeleteRole(row) {
            const schoolId = this.positiveDeptId(this.DepartmentId);
            if (!schoolId) return;
            this.$confirm(`确认删除角色「${row.Name}」吗？`, '提示', { type: 'warning' }).then(() => {
                deleteEvaluationRole({ id: row.Id, schoolDepartmentId: schoolId }).then(res => {
                    if (res.data && res.data.success) {
                        this.$message.success('删除成功');
                        if (this.currentRole && this.currentRole.Id === row.Id) {
                            this.currentRole = null;
                            this.members = [];
                        }
                        this.loadRoles();
                    } else {
                        this.$message.error((res.data && res.data.msg) || '删除失败');
                    }
                });
            }).catch(() => { });
        },
        handleQuickImport(sourceType) {
            const schoolId = this.positiveDeptId(this.DepartmentId);
            if (!schoolId || !this.currentRole) return;
            this.importBrowseSourceType = sourceType;
            this.importBrowseKeyword = '';
            this.importBrowsePage = 1;
            this.importCandidates = [];
            this.importSelectedMasterIds = [];
            this.importBrowseVisible = true;
            this.importBrowseLoading = true;
            previewQuickImportEvaluationRoleMembers({
                SchoolDepartmentId: schoolId,
                RoleId: this.currentRole.Id,
                SourceType: sourceType
            }).then(res => {
                this.importBrowseLoading = false;
                if (res.data && res.data.success) {
                    this.importCandidates = res.data.response || [];
                    this.importBrowsePage = 1;
                    this.importSelectedMasterIds = this.importCandidates
                        .filter(item => !item.IsAlreadyMember)
                        .map(item => item.TeacherMasterId)
                        .filter(Boolean);
                    this.syncImportTableSelection();
                    if (!this.importCandidates.length) {
                        this.$message.info((res.data && res.data.msg) || '未找到可导入的人员');
                    }
                } else {
                    this.importCandidates = [];
                    this.importSelectedMasterIds = [];
                    this.$message.error((res.data && res.data.msg) || '加载候选人员失败');
                }
            }).catch(() => {
                this.importBrowseLoading = false;
                this.importCandidates = [];
                this.importSelectedMasterIds = [];
            });
        },
        handleImportBrowseSearch() {
            this.importBrowsePage = 1;
            this.syncImportTableSelection();
        },
        handleImportBrowsePageChange(page) {
            this.importBrowsePage = page || 1;
            this.syncImportTableSelection();
        },
        handleImportBrowseSizeChange(size) {
            this.importBrowsePageSize = size || 10;
            this.importBrowsePage = 1;
            this.syncImportTableSelection();
        },
        isImportCandidateSelectable(row) {
            return !!(row && !row.IsAlreadyMember);
        },
        /** 跨页勾选：合并当前页选中状态到全局勾选集合 */
        handleImportSelectionChange(selection) {
            if (this.importSelectionSyncing) return;
            const pageIds = (this.pagedImportCandidates || [])
                .filter(item => !item.IsAlreadyMember)
                .map(item => item.TeacherMasterId);
            const selectedOnPage = new Set((selection || []).map(item => item.TeacherMasterId));
            const kept = (this.importSelectedMasterIds || []).filter(id => pageIds.indexOf(id) === -1);
            this.importSelectedMasterIds = kept.concat(
                pageIds.filter(id => selectedOnPage.has(id))
            );
        },
        /** 翻页/搜索后恢复当前页勾选状态 */
        syncImportTableSelection() {
            this.$nextTick(() => {
                const table = this.$refs.importBrowseTable;
                if (!table) return;
                this.importSelectionSyncing = true;
                table.clearSelection();
                const selectedSet = new Set(this.importSelectedMasterIds || []);
                (this.pagedImportCandidates || []).forEach(row => {
                    if (!row.IsAlreadyMember && selectedSet.has(row.TeacherMasterId)) {
                        table.toggleRowSelection(row, true);
                    }
                });
                this.$nextTick(() => {
                    this.importSelectionSyncing = false;
                });
            });
        },
        /** 移除后校正当前页，避免页码越界 */
        normalizeImportBrowsePage() {
            const total = (this.filteredImportCandidates || []).length;
            const pageSize = this.importBrowsePageSize || 10;
            const maxPage = Math.max(1, Math.ceil(total / pageSize) || 1);
            if (this.importBrowsePage > maxPage) {
                this.importBrowsePage = maxPage;
            }
        },
        removeImportCandidate(row) {
            if (!row || row.IsAlreadyMember) return;
            const masterId = row.TeacherMasterId;
            this.importCandidates = (this.importCandidates || []).filter(item => item.TeacherMasterId !== masterId);
            this.importSelectedMasterIds = (this.importSelectedMasterIds || []).filter(id => id !== masterId);
            this.$nextTick(() => {
                this.normalizeImportBrowsePage();
                this.syncImportTableSelection();
            });
        },
        submitImportBrowse(importAll) {
            const schoolId = this.positiveDeptId(this.DepartmentId);
            if (!schoolId || !this.currentRole) return;
            const pendingIds = (this.importCandidates || [])
                .filter(item => !item.IsAlreadyMember)
                .map(item => item.TeacherMasterId)
                .filter(Boolean);
            const pendingSet = new Set(pendingIds);
            const teacherMasterIds = importAll
                ? pendingIds
                : (this.importSelectedMasterIds || []).filter(id => pendingSet.has(id));
            if (!teacherMasterIds.length) {
                this.$message.warning(importAll ? '暂无待导入人员' : '请先勾选要导入的人员');
                return;
            }
            this.importBrowseSubmitLoading = true;
            saveEvaluationRoleMembers({
                SchoolDepartmentId: schoolId,
                RoleId: this.currentRole.Id,
                TeacherMasterIds: teacherMasterIds
            }).then(res => {
                this.importBrowseSubmitLoading = false;
                if (res.data && res.data.success) {
                    this.$message.success(res.data.msg || '导入成功');
                    this.importBrowseVisible = false;
                    this.loadRoles();
                    this.loadMembers();
                } else {
                    this.$message.error((res.data && res.data.msg) || '导入失败');
                }
            }).catch(() => {
                this.importBrowseSubmitLoading = false;
            });
        },
        handleAddMembers() {
            if (!this.currentRole) return;
            const schoolId = this.positiveDeptId(this.DepartmentId);
            if (!schoolId) {
                this.$message.warning('请先选择学校');
                return;
            }
            this.memberPickerVisible = true;
        },
        /** 内部通讯录多选后写入当前角色。最后修改：2026-09-25 */
        onMembersPicked(people) {
            const schoolId = this.positiveDeptId(this.DepartmentId);
            if (!schoolId || !this.currentRole || this.memberSubmitLoading) return;
            const teacherMasterIds = (Array.isArray(people) ? people : [])
                .map(item => Number(item && item.id))
                .filter(id => id > 0);
            if (!teacherMasterIds.length) {
                this.$message.warning('请选择教职工');
                return;
            }
            this.memberSubmitLoading = true;
            saveEvaluationRoleMembers({
                SchoolDepartmentId: schoolId,
                RoleId: this.currentRole.Id,
                TeacherMasterIds: teacherMasterIds
            }).then(res => {
                this.memberSubmitLoading = false;
                if (res.data && res.data.success) {
                    this.$message.success(res.data.msg || '保存成功');
                    this.loadRoles();
                    this.loadMembers();
                } else {
                    this.$message.error((res.data && res.data.msg) || '保存失败');
                }
            }).catch(() => {
                this.memberSubmitLoading = false;
            });
        },
        handleRemoveMembers() {
            const schoolId = this.positiveDeptId(this.DepartmentId);
            if (!schoolId || !this.currentRole || !this.selectedMemberIds.length) return;
            this.$confirm(`确认移除选中的 ${this.selectedMemberIds.length} 名人员吗？`, '提示', { type: 'warning' })
                .then(() => {
                    this.memberLoading = true;
                    removeEvaluationRoleMembers({
                        SchoolDepartmentId: schoolId,
                        RoleId: this.currentRole.Id,
                        MemberIds: this.selectedMemberIds
                    }).then(res => {
                        this.memberLoading = false;
                        if (res.data && res.data.success) {
                            this.$message.success(res.data.msg || '移除成功');
                            this.loadRoles();
                            this.loadMembers();
                        } else {
                            this.$message.error((res.data && res.data.msg) || '移除失败');
                        }
                    }).catch(() => {
                        this.memberLoading = false;
                    });
                }).catch(() => { });
        }
    }
};
</script>

<style scoped>
.erm-page > .dp-right.erm-workspace {
    display: flex;
    flex-direction: row;
    gap: 12px;
    min-height: 0;
    height: 100%;
    overflow: hidden;
    padding: 0;
}

.erm-role-pane {
    width: 300px;
    min-width: 280px;
    max-width: 320px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    min-height: 0;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    overflow: hidden;
}

.erm-member-pane {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    overflow: hidden;
}

.erm-pane-head,
.erm-member-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 14px 10px;
    border-bottom: 1px solid #eef2f6;
    flex-shrink: 0;
}

.erm-pane-head__title,
.erm-member-head__title {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    line-height: 1.4;
}

.erm-member-head__title {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.erm-pane-head__sub,
.erm-member-head__sub {
    margin-top: 2px;
    font-size: 12px;
    color: #64748b;
}

.erm-member-head__sub b {
    color: var(--btn-primary, #2563eb);
}

.erm-member-head__actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
    flex-shrink: 0;
}

.erm-pane-search {
    padding: 10px 10px 8px;
    flex-shrink: 0;
}

.erm-role-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 0 8px 10px;
}

.erm-role-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    margin-bottom: 4px;
    padding: 7px 8px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: transparent;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;
}

.erm-role-item:hover {
    background: #f8fafc;
}

.erm-role-item.is-active {
    border-color: #bfdbfe;
    background: #eff6ff;
}

.erm-role-item.is-disabled {
    border-color: transparent;
    background: #fafafa;
}

.erm-role-item.is-disabled:hover {
    background: #f5f5f5;
}

.erm-role-item.is-disabled.is-active {
    border-color: transparent;
    background: #f1f5f9;
}

.erm-role-item.is-disabled .erm-role-item__name {
    color: #64748b;
}

.erm-role-item.is-disabled .erm-role-item__meta {
    color: #94a3b8;
    opacity: 1;
}

.erm-role-item.is-disabled .erm-role-item__avatar {
    background: #e2e8f0;
    color: #64748b;
}

.erm-role-item.is-disabled.is-active .erm-role-item__avatar {
    background: #cbd5e1;
    color: #475569;
}

.erm-role-item__status-tag {
    flex-shrink: 0;
    font-weight: 600;
    letter-spacing: 0.02em;
}

.erm-role-item__avatar {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #e0e7ff;
    color: var(--btn-primary, #2563eb);
    font-size: 13px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.erm-role-item.is-active .erm-role-item__avatar {
    background: var(--btn-primary, #2563eb);
    color: #fff;
}

.erm-role-item__body {
    flex: 1 1 auto;
    min-width: 0;
}

.erm-role-item__name {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    line-height: 1.35;
}

.erm-role-item__meta {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    color: #64748b;
    line-height: 1.35;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.erm-role-item__more {
    width: 24px;
    height: 24px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.15s ease, color 0.15s ease;
}

.erm-role-item__more:hover {
    background: #f1f5f9;
    color: #475569;
}

.erm-role-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    color: var(--btn-primary, #2563eb);
    background: #eff6ff;
}

.erm-search-input {
    width: 200px;
}

.erm-member-table {
    flex: 1 1 auto;
    min-height: 0;
    height: 0;
    width: 100%;
    overflow: hidden;
}

.erm-member-table /deep/ .el-table {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.erm-member-table /deep/ .el-table__header-wrapper {
    flex-shrink: 0;
    overflow: hidden !important;
}

.erm-member-table /deep/ .el-table__body-wrapper {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto !important;
}

.erm-member-table /deep/ .el-table__body tr {
    cursor: pointer;
}

.erm-member-body {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

.erm-member-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px 8px;
    flex-shrink: 0;
}

.erm-role-actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-left: 4px;
    padding-left: 8px;
    border-left: 1px solid #e2e8f0;
}

.erm-allstaff-tip {
    margin: 0 14px 8px;
    padding: 8px 10px;
    border-radius: 8px;
    background: #f8fafc;
    color: #64748b;
    font-size: 12px;
    line-height: 1.5;
}

.erm-import-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
}

.erm-import-count {
    font-size: 12px;
    color: #64748b;
    white-space: nowrap;
}

.erm-import-count b {
    color: var(--btn-primary, #2563eb);
}

.erm-import-table {
    min-height: 360px;
}

.erm-import-pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
}

.erm-pagination {
    display: flex;
    justify-content: flex-end;
    padding: 8px 14px 10px;
    border-top: 1px solid #eef2f6;
    flex-shrink: 0;
}

.erm-hint {
    color: #94a3b8;
    font-size: 12px;
}

.dialog-footer {
    text-align: right;
}

.erm-member-body /deep/ .el-empty,
.erm-role-list /deep/ .el-empty {
    padding: 20px 8px;
}

@media screen and (max-width: 900px) {
    .erm-page > .dp-right.erm-workspace {
        flex-direction: column !important;
        height: auto !important;
        overflow: visible !important;
    }

    .erm-role-pane {
        width: 100%;
        max-width: none;
        max-height: 260px;
    }

    .erm-member-pane {
        min-height: 360px;
    }

    .erm-member-head {
        flex-direction: column;
        align-items: stretch;
    }

    .erm-member-head__actions {
        justify-content: flex-start;
    }

    .erm-search-input {
        width: 100%;
        max-width: none;
    }
}

@media screen and (max-width: 768px) {
    .erm-page > .dp-right.erm-workspace {
        display: block !important;
    }

    .erm-role-pane,
    .erm-member-pane {
        max-height: none;
        min-height: 0;
        margin-bottom: 12px;
        overflow: visible;
    }

    .erm-role-list {
        max-height: 240px;
        overflow-y: auto;
    }

    .erm-member-table,
    .erm-member-body {
        height: auto !important;
        overflow: visible !important;
    }

    .erm-member-table /deep/ .el-table,
    .erm-member-table /deep/ .el-table__body-wrapper {
        height: auto !important;
        display: block !important;
        overflow-x: auto !important;
        overflow-y: visible !important;
    }
}
</style>
